import { NgInject } from '@df/ng/ng-inject';
import { App } from '@df/core/app';
import { Subject, Subscription } from 'rxjs';
import { Model } from '@df/catalog/model';
import { Listing } from '@df/catalog-ui/listing/listing';

export interface IFilterConfig<I = string> {
    id: I;

    /**
     * UI if filter is open
     */
    isOpen: boolean;

    /**
     * UI Filter position
     */
    position: number;

    /**
     * FIlter type
     */
    type: TFilterType;
}

export type TFilterType = string | 'category' | 'query';

/**
 * General abstract filter class to be used with listing
 */
export abstract class Filter<T extends Model, S = any, I = number | string, C extends IFilterConfig<I> = IFilterConfig<I>> {
    /**
     * Changes subject
     */
    protected subject = new Subject<S | undefined>();

    /**
     * Current selection
     */
    protected _selection?: S;

    /**
     * Filter type set by listingFilter decorator
     */
    filterType!: string;

    /**
     * Checks if filter has any matches
     */
    abstract hasAnyMatches: boolean;

    /**
     * Checks if selection is set
     */
    abstract hasSelection: boolean;

    @NgInject(App)
    readonly app!: App;

    private subscriptions = new Subscription();

    constructor(
        /**
         * Filter config
         */
        public config: C,
        /**
         * Parent listing
         * @type {Listing}
         */
        public listing: Listing<T>,
        /**
         * Initial data to set
         * @param {S}
         */
        selection?: S
    ) {
        if (selection) {
            this.selection = selection;
        }
        this.onInit();
    }

    /**
     * Registers callback triggered when sort option changes
     */
    subscribe(...args): Subscription {
        const subscription = this.subject.subscribe(...args);
        this.subscriptions.add(subscription);
        return subscription;
    }

    /**
     * Trigggers all registered callbacks
     */
    next() {
        this.subject.next(this.selection);
    }

    /**
     * On init hook
     */
    protected onInit() {}

    /**
     * Id getter
     */
    get id(): I {
        return this.config.id;
    }

    /**
     * Current selection setter
     */
    set selection(selection: S | undefined) {
        this._selection = selection;
    }

    /**
     * Current selection getter
     */
    get selection(): S | undefined {
        return this._selection;
    }

    get isDefaultOpen(): boolean {
        return this.config.isOpen;
    }

    /**
     * Serializatio selection to string
     */
    abstract getState(): any;

    /**
     * Unserializes selection from string
     */
    abstract setState(data: any): void;

    /**
     * Method applying filter on model
     */
    abstract applyFilter(models: T[], selectionOverride?: S): T[];

    /**
     * Methods reseting filter
     */
    abstract reset(): void;

    /**
     * Checks if filter has any current matches
     */
    hasManyCurrentMatches(): boolean {
        return true;
    }

    destroy() {
        this.subscriptions?.unsubscribe();
    }
}
